import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/site/Seo';
import { ProcessFlowDiagram } from '../components/ui/ProcessFlowDiagram';
import { useContentfulAssets } from '../hooks/useContentfulAssets';

function ScrollReveal({ children, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); observer.unobserve(node); } }, { threshold: 0.15 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className={`scroll-reveal-group ${visible ? 'is-visible' : ''} ${className}`.trim()}>{children}</div>;
}

const HERO_METRICS = [
  { value: '0.05', unit: 'mm', prefix: '±', label: 'Repeatability' },
  { value: '0.25', unit: 'mm', prefix: '±', label: 'Accuracy' },
  { value: '8', unit: '', label: 'Dexterous axes' },
  { type: 'pulse', text: '∞', label: 'Linear scalability' },
];

function AnimatedNumber({ value }) {
  const [display, setDisplay] = React.useState(0);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const target = parseFloat(value);
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        const duration = 1200;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(target * eased);
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  const decimalPlaces = value.includes('.') ? value.split('.')[1].length : 0;
  const formatted = decimalPlaces > 0 ? display.toFixed(decimalPlaces) : Math.round(display).toString();
  return <span ref={ref} className='hero-metric-value'>{formatted}</span>;
}

function ScaleDownMetric() {
  const units = ['m', 'cm', 'mm', 'µm'];
  const [index, setIndex] = React.useState(0);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        let i = 0;
        const interval = setInterval(() => {
          i++;
          if (i >= units.length) { clearInterval(interval); return; }
          setIndex(i);
        }, 600);
        observer.disconnect();
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <span ref={ref} className='hero-metric-value hero-metric-scaledown'>{units[index]}</span>;
}

function PulseMetric({ text }) {
  return <span className='hero-metric-value hero-metric-pulse'>{text}</span>;
}

function HeroMetrics() {
  return (
    <div className='hero-metrics'>
      {HERO_METRICS.map((m) => (
        <div key={m.label} className='hero-metric'>
          <div className='hero-metric-number'>
            {m.value ? (
              <>
                {m.prefix && <span className='hero-metric-unit'>{m.prefix}</span>}
                <AnimatedNumber value={m.value} />
                <span className='hero-metric-unit'>{m.unit}</span>
              </>
            ) : m.type === 'scaleDown' ? (
              <ScaleDownMetric />
            ) : m.type === 'pulse' ? (
              <PulseMetric text={m.text} />
            ) : (
              <span className='hero-metric-value'>{m.text}</span>
            )}
          </div>
          <span className='hero-metric-label'>{m.label}</span>
        </div>
      ))}
    </div>
  );
}

const AT_A_GLANCE = [
  { icon: '◎', title: 'Force Control', body: '6-DoF tactile feedback for every tool and material combination.', anchor: '#force-control' },
  { icon: '⚙', title: 'Calibration', body: 'Autonomous kinematic calibration to 12 µm accuracy.', anchor: '#calibration' },
  { icon: '◉', title: 'Teach GUI', body: 'Visual robot programming through CAD interaction.', anchor: '#teach-gui' },
  { icon: '⊕', title: 'Real-Time Optimisation', body: 'Conditional execution and closed-loop rework from sensor feedback.', anchor: '#optimisation' },
];

const FORCE_CONTROL_MODALITIES = [
  { title: 'Milling', body: 'Inspired by manual die-grinding. Forces are the primary feedback to ensure adequate cutting edge engagement. Paths update in real time to keep desired engagement force.' },
  { title: 'Grinding', body: 'Applies known forces that maximise abrasive engagement and tool lifespan. Detects high spots and spends more time grinding them, just the way a skilled person would.' },
  { title: 'Polishing & Engraving', body: 'Applies constant pressure with conventional tools via the Tactile Platform. Outputs accurate topological maps based on deflection required to maintain contact pressure.' },
  { title: 'Cutting & Drilling', body: 'Adjusts feed rate in real time based on force feedback. Differentiates plunging force from shearing force, reducing the risk of discs binding or overheating.' },
  { title: 'Hammering', body: 'Only hammers when supports are detected and the chisel is preloaded to optimal fracture force. Adapts execution speed to material ductility batch-to-batch.' },
];

const CALIBRATION_STEPS = [
  { count: 1, title: 'Kinematic chain', body: 'Automatic calibration of the 8-axis manipulator using optical measurement to 12 µm accuracy. No external sensors or manual intervention.', imageKey: 'image2' },
  { count: 2, title: 'Measurement devices', body: 'Break beam sensors and contact tool setters calibrated relative to the robot frame for tool length, wear and damage detection.', imageKey: 'image3' },
  { count: 3, title: 'Scanner origin', body: 'Hand-eye calibration aligns 3D scanner frames to the robot coordinate system. Enables scan-based part location and reconfigurable fixturing.', imageKey: 'image21' },
  { count: 4, title: 'Tool centre point', body: 'Each tool type (spindles, belts, chisels, grippers) calibrated to complete the kinematic chain from cutting edge to component.', imageKey: 'image26' },
];

const OPTIMISATION_FEATURES = [
  { count: 1, title: 'Detect & measure', body: 'Material detection returns presence or absence. Measurement steps return geometric dimensions or surface roughness values from optical instruments.' },
  { count: 2, title: 'Conditional execution', body: 'Any recipe step can be assigned conditions for execution. Binary conditions specify which outcome triggers the step. Measurement conditions specify threshold values.' },
  { count: 3, title: 'Closed-loop rework', body: 'Condition check linked to a rework step or recipe. After rework, re-run the check. Continue until the condition is met, then move on.' },
];

const COMPATIBLE_PLATFORMS = [
  {
    title: 'r1000',
    body: 'The production microfactory. Adaptive scanning, toolpath control, and finishing intelligence in one compact system.',
    href: '/solutions/r1000',
  },
  {
    title: 'r500',
    body: 'The compact route. Same NetShape® intelligence in a smaller footprint. Request Early Access.',
    href: '/solutions/r500',
  },
];

export function TechnologyPage() {
  const { items: netshapeImages } = useContentfulAssets('netshape/', 'netshapeImages');

  return (
    <>
      <Seo
        title='Technologies | Rivelin Robotics'
        description="NetShape® is Rivelin's proprietary autonomous finishing software. It sees, adapts, and finishes on any Rivelin platform."
      />
      <div className='page-shell'>
        <section className='page-intro'>
          <div className='page-intro-copy'>
            <h1>The NetShape<sup>®</sup> software platform.</h1>
            <p className='page-intro-body'>
              Physical AI for robotic finishing. Machine vision, proprioception and tactile feedback combined into a single control platform. NetShape<sup>®</sup> removes the skill barrier from robotic finishing.
            </p>
          </div>
        </section>

        {/* At a glance - section nav */}
        <section className='content-section'>
          <div className='glance-band'>
            {AT_A_GLANCE.map((item) => (
              <a key={item.title} className='glance-card' href={item.anchor}>
                <span className='glance-icon'>{item.icon}</span>
                <span className='glance-title'>{item.title}</span>
                <p className='glance-body'>{item.body}</p>
              </a>
            ))}
          </div>
        </section>

        <HeroMetrics />

        <section className='content-section theme-soft' id='force-control'>
          <div className='section-header'>
            <p className='section-label'>Tactile Feedback and Force Control</p>
            <h2>Like skilled human technicians, Rivelin robots know their tools.</h2>
            <p className='section-copy'>
              6-DoF force transducers feel contact between tools and components. How the robot responds depends on the tool type, material and objective of the operation. 9+ years of force control research on low-stiffness robotic systems.
            </p>
          </div>
          <div className='force-modality-list'>
            {FORCE_CONTROL_MODALITIES.map((item) => (
              <div key={item.title} className='force-modality-item'>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Calibration - numbered steps */}
        <section className='content-section' id='calibration'>
          <div className='section-header'>
            <p className='section-label'>In-Process Kinematic Calibration</p>
            <h2>No external sensors. No manual intervention.</h2>
            <p className='section-copy'>
              Accuracy relies on a close match between virtual and physical systems. Rivelin robots explore the machining envelope with an optical sensor capable of measuring 3D poses to within 12 micrometres, then use proprietary regression algorithms to compute the kinematic chain.
            </p>
          </div>
          <div className='capabilities-grid'>
            {CALIBRATION_STEPS.map((step) => {
              const img = netshapeImages.find((i) => i.label === step.imageKey);
              return (
                <article key={step.title} className='capabilities-tile'>
                  {img && <img className='capabilities-tile-image' src={img.image} alt={step.title} loading='lazy' />}
                  <span className='capabilities-tile-label'>{step.title}</span>
                  <p className='capabilities-tile-detail'>{step.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* Teach GUI - big statement + body */}
        <section className='content-section theme-soft' id='teach-gui'>
          <div className='section-split'>
            <div className='section-header'>
              <p className='section-label'>NetShape<sup>®</sup> Teach GUI</p>
              <h2>Translate experience into robot recipes.</h2>
              <p className='section-copy'>
                Developed with ease of use as a core driver. Any technician or graduate can plan and execute robotic crafting recipes through visual interactions with the component CAD model. No robotics or CNC skills required.
              </p>
            </div>
            {(() => {
              const img = netshapeImages.find((i) => i.label === 'image3');
              if (!img) return null;
              return (
                <div className='section-media'>
                  <img src={img.image} alt='NetShape Teach GUI' loading='lazy' />
                </div>
              );
            })()}
          </div>
          <div className='capabilities-grid'>
            {[
              { title: 'Visual interactions', detail: 'Define vectors, curves, surfaces and contours directly on the component CAD. Click-and-steer, paint-brush selection and spline fitting.', imageKey: 'image23' },
              { title: 'Recipe step builder', detail: 'Guided sequence of pages with fields pre-filled from Rivelin\'s force control knowledge library. Modular by design.', imageKey: 'image24' },
              { title: 'Component manifests', detail: 'Describe parts across multiple process states (as-printed, partial supports, finished) each with its own fixture and CAD.', imageKey: 'image20' },
              { title: 'Projects & recipes', detail: 'Lists of recipe steps in recipes, lists of recipes in projects. Simulate, reorder, conditionally execute.', imageKey: 'image17' },
            ].map((item) => {
              const img = netshapeImages.find((i) => i.label === item.imageKey);
              return (
                <article key={item.title} className='capabilities-tile'>
                  {img && <img className='capabilities-tile-image' src={img.image} alt={item.title} loading='lazy' />}
                  <span className='capabilities-tile-label'>{item.title}</span>
                  <p className='capabilities-tile-detail'>{item.detail}</p>
                </article>
              );
            })}
          </div>
        </section>

        {/* Real-Time Optimisation - numbered steps */}
        <section className='content-section' id='optimisation'>
          <div className='section-header'>
            <p className='section-label'>Real-Time Optimisation</p>
            <h2>Feedback from sensors to make decisions.</h2>
            <p className='section-copy'>
              Conditional execution of recipe steps based on measurements and closed-loop rework linking measurements to rework steps. Improves efficiency and removes variability from outcomes.
            </p>
          </div>
          <ScrollReveal>
          <div className='how-we-work-steps'>
            {OPTIMISATION_FEATURES.map((step, i) => (
              <div key={step.count} className='how-we-work-step reveal-item' style={{ '--reveal-delay': `${i * 150}ms` }}>
                <div className='how-we-work-step-marker'>
                  <span className='process-count'>{step.count}</span>
                  {step.count < OPTIMISATION_FEATURES.length && <span className='how-we-work-step-line' />}
                </div>
                <div className='how-we-work-step-content'>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </div>
              </div>
            ))}
          </div>
          </ScrollReveal>
        </section>

        {/* Process Flow */}
        <section className='content-section'>
          <div className='section-header'>
            <p className='section-label'>How it works</p>
            <h2>From CAD to production recipe.</h2>
            <p className='section-label'><AnimatedNumber value='20' /> days from CAD to production recipe</p>
          </div>
          <ProcessFlowDiagram className='process-flow-diagram' />
        </section>

        {/* Compatible platforms */}
        <section className='content-section theme-soft'>
          <div className='section-header'>
            <p className='section-label'>Compatible platforms</p>
            <h2>NetShape runs on every Rivelin microfactory.</h2>
          </div>
          <div className='product-chooser-grid'>
            {COMPATIBLE_PLATFORMS.map((platform) => (
              <article key={platform.title} className='product-chooser-card'>
                <div className='product-chooser-copy' style={{ padding: '1.5rem' }}>
                  <h3>{platform.title}</h3>
                  <p>{platform.body}</p>
                  <Link className='primary-cta product-chooser-action' to={platform.href}>
                    Explore {platform.title}
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </section>

      </div>
    </>
  );
}
